import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { BlogPost } from '../data/blogPosts';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface RelatedPostsCarouselProps {
  currentPostId: number;
  posts: BlogPost[];
}

const RelatedPostsCarousel: React.FC<RelatedPostsCarouselProps> = ({ currentPostId, posts }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [postsPerView, setPostsPerView] = useState(3);
  const containerRef = useRef<HTMLDivElement>(null);
  const [cardWidth, setCardWidth] = useState(0);

  const relatedPosts = posts
    .filter(post => post.id !== currentPostId.toString())
    .slice(0, 6);

  useEffect(() => {
    const update = () => {
      const ppv = window.innerWidth >= 768 ? 3 : 1;
      setPostsPerView(ppv);
      if (containerRef.current) {
        setCardWidth(containerRef.current.offsetWidth / ppv);
      }
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  const maxPages = Math.ceil(relatedPosts.length / postsPerView);

  useEffect(() => {
    if (currentIndex >= maxPages) setCurrentIndex(Math.max(0, maxPages - 1));
  }, [maxPages, currentIndex]);

  if (relatedPosts.length < 1) return null;

  const xOffset = -(currentIndex * postsPerView * cardWidth);

  return (
    <div className="py-8 bg-gray-50 rounded-lg">
      <h2 className="text-2xl font-semibold text-[#2a3c5c] text-center mb-6">
        Leia também
      </h2>

      <div className="relative px-10">
        {/* Setas */}
        {maxPages > 1 && (
          <>
            <button
              onClick={() => setCurrentIndex(i => (i - 1 + maxPages) % maxPages)}
              className="absolute left-1 top-1/2 -translate-y-1/2 z-10 bg-white hover:bg-gray-50 p-2 rounded-full shadow-md text-[#2a3c5c]"
              aria-label="Anterior"
            >
              <ChevronLeft size={22} />
            </button>
            <button
              onClick={() => setCurrentIndex(i => (i + 1) % maxPages)}
              className="absolute right-1 top-1/2 -translate-y-1/2 z-10 bg-white hover:bg-gray-50 p-2 rounded-full shadow-md text-[#2a3c5c]"
              aria-label="Próximo"
            >
              <ChevronRight size={22} />
            </button>
          </>
        )}

        {/* Track */}
        <div ref={containerRef} className="overflow-hidden">
          <motion.div
            className="flex"
            animate={{ x: xOffset }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          >
            {relatedPosts.map(post => (
              <div
                key={post.id}
                style={{ width: `${cardWidth}px`, flexShrink: 0 }}
                className="px-2"
              >
                <Link
                  to={`/blog/${post.slug}`}
                  className="block h-full"
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                >
                  <div className="bg-white rounded-lg shadow-sm overflow-hidden h-full flex flex-col hover:shadow-md transition-shadow duration-300">
                    <div className="h-40 overflow-hidden">
                      <img
                        src={post.coverImage}
                        alt={post.title}
                        className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                      />
                    </div>
                    <div className="p-4 flex-grow flex flex-col">
                      <span className="text-xs text-gray-500 mb-1">
                        {new Date(post.date).toLocaleDateString('pt-BR')}
                      </span>
                      <h3 className="font-medium text-[#2a3c5c] mb-3 line-clamp-2 text-sm">
                        {post.title}
                      </h3>
                      <div className="mt-auto flex flex-wrap gap-1">
                        {post.tags.slice(0, 2).map((tag, i) => (
                          <span
                            key={i}
                            className="bg-gray-100 text-gray-600 text-xs px-2 py-0.5 rounded-full"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </Link>
              </div>
            ))}
          </motion.div>
        </div>

        {/* Dots */}
        {maxPages > 1 && (
          <div className="flex justify-center mt-5 gap-2">
            {Array.from({ length: maxPages }).map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrentIndex(i)}
                className={`h-2 rounded-full transition-all duration-300 ${
                  i === currentIndex ? 'bg-[#2a3c5c] w-6' : 'bg-gray-300 hover:bg-gray-400 w-2'
                }`}
                aria-label={`Página ${i + 1}`}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default RelatedPostsCarousel;
